import memoize from 'fast-memoize';
import { action, computed, observable } from 'mobx';
import i18n from '../../../i18n';
import { PermissionService } from '../api/permission-service';
import { MetricId, ReadData } from '../graphql/generated/graphql-sdk';
import { enumValues } from '../utilFunctions/pure-utils';
import { splitViewMetricTag } from '../utilFunctions/utils';
import { Hasher } from '../v2/api/hasher';
import { AssignedPermissions as V2AssignedPermissions } from '../v2/api/permission/types';
import { HasDependencies, hashString } from '../v2/api/types';
import { DomainScopedErrorTracker } from '../v2/common/components/errors/trackers/DomainScopedErrorTracker';
import { ErrorTypes } from '../v2/common/components/errors/types';
import { Domains } from '../v2/constants';
import { DataFieldWithDataType } from '../v2/types';
import {
  AssignedPermissions,
  Permission,
  PermissionTypes,
  Role,
  RoleId,
  ViewMetricsTagType,
} from './permissions';

interface RoleWithPermissions {
  role: Role;
  directPermissions: AssignedPermissions<Permission>;
}

export interface IPermissionsStore extends HasDependencies {
  readonly canManageAliases: boolean;

  //Datafields
  isFieldAllowedForExecutorRole(field: DataFieldWithDataType): boolean;

  // Metrics
  canViewMetric(metricId: MetricId): boolean;

  // Administration
  readonly canManageAllDomains: boolean; // This includes all permissions to manage all domains and e.g. their allowed dashboards
  readonly userPermissionsCanManageAllDomains: boolean; // like canManageAllDomains but always uses user permissions and never simulated permissions
  canChangeUserStatus(): boolean;
  canSeePrivateUserConfig(): boolean; // For now this includes only reporting-line-flag, but later also last-login and roles etc.
  canManageOwnUserAccount(): boolean;

  // Reports
  canExportReports(): boolean;

  // Impersonation
  permissionsError: boolean;

  // Upload data
  canUploadData(): boolean;
  // Delete data
  canDeleteData(): boolean;
  // Download data
  canDownloadClientData(): boolean;

  // Role Attributes
  getNonUserRoleName(name: Role): string;
  isReservedRoleName(name: string): boolean;
  isRoleEditable(roleWithPermissions: RoleWithPermissions): boolean;
  isRoleDeletable(roleWithPermissions: RoleWithPermissions): boolean;
  isRoleCopyable(roleWithPermissions: RoleWithPermissions): boolean;

  // roleScope
  canViewRoleScopeForRole(roleId: string): boolean;
  isRoleEditableScopeForRole(roleId: string): boolean;
  canAccessMyFilterGroups(): boolean;

  // permission pages
  canReadUserPermissions(): boolean;
  canWriteUserPermissions(): boolean;
  canReadRoleSettings(): boolean;
  canWriteRoleSettings(): boolean;

  /**
   * Determines whether the user can view technical datapipeline DAG information in the data upload object processing history.
   */
  canViewDatapipelineDag(): boolean;
}

export enum RoleTypes {
  USER_ROLE = 'USER_ROLE',
  NON_USER_ROLE = 'NON_USER_ROLE',
}
export interface RoleWithRoleType extends Role {
  roleType: RoleTypes;
}

export enum ReservedRoleNames {
  SUPERADMIN = 'SUPERADMIN',
  ADMIN = 'ADMIN',
  GLOBAL_ACCESS = 'Global Access',
}
export class PermissionBasedPermissionsStore implements IPermissionsStore {
  private isFieldAllowedInPermission = memoize(
    (field: DataFieldWithDataType, permission?: ReadData) => {
      const hasFullAccess = permission ? permission.access === null : false;
      const hasSpecificFieldAccess =
        permission?.access?.some(
          (a) =>
            a.datatype === field.dataType &&
            (a.fields?.specificDatafields?.some(
              (f) => f.datafield === field.dataField
            ) ??
              false)
        ) ?? false;
      return hasFullAccess || hasSpecificFieldAccess;
    }
  );

  public isFieldAllowedForExecutorRole = (
    field: DataFieldWithDataType
  ): boolean => {
    const executorRoleReadDataPermissions = this.executorRolePermissions
      .filterByType(PermissionTypes.ReadData)
      .first();
    return this.isFieldAllowedInPermission(
      field,
      executorRoleReadDataPermissions?.permission
    );
  };

  @computed
  private get canViewAllMetrics(): boolean {
    const metricPermission = this.executorRolePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.ViewMetrics)
      .first()?.permission;

    if (metricPermission) {
      return metricPermission.allowedTags === null;
    } else {
      return false;
    }
  }

  @computed
  private get allowedSingleMetricTags(): MetricId[] {
    const metricPermission = this.executorRolePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.ViewMetrics)
      .first()?.permission;

    return (metricPermission?.allowedTags ?? [])
      .map((tag) => {
        const parsedViewMetricTag = splitViewMetricTag(tag);
        if (
          parsedViewMetricTag &&
          parsedViewMetricTag.tagType === ViewMetricsTagType.SINGLE
        ) {
          return parsedViewMetricTag.tagValue as MetricId;
        } else {
          const error = new Error(
            `some kind of metric tag type that we don't support yet. Should log sentry error. Tag is - ${tag}`
          );
          console.error(error.message);
          this.errorTracker.trackError({
            error,
            errorType: ErrorTypes.METRIC_MISMATCH,
          });
          return null;
        }
      })
      .flatMap((elem) => (elem ? [elem] : []));
  }

  public canViewMetric = memoize((metricId: MetricId): boolean => {
    return (
      this.canViewAllMetrics || this.allowedSingleMetricTags.includes(metricId)
    );
  });

  @observable
  public permissionsError = false;

  public constructor(
    readonly permissionService: PermissionService,
    readonly executorRolesFromFrontendStartup: Role[],
    readonly effectiveRoleId: RoleId,
    readonly effectivePermissions: V2AssignedPermissions<Permission>,
    readonly executorRolePermissions: V2AssignedPermissions<Permission>,
    readonly domain: Domains,
    readonly errorTracker: DomainScopedErrorTracker
  ) {}

  public getDeps = () => [
    hashString([
      ...this.permissionService.getDeps(),
      Hasher.hash(this.executorRolesFromFrontendStartup),
      this.effectiveRoleId,
      Hasher.hash(this.effectivePermissions),
      Hasher.hash(this.executorRolePermissions),
      this.domain,
      ...this.errorTracker.getDeps(),
    ]),
  ];

  @action
  public checkMinimumPermissions = () => {
    const validMinimumPermissionSets = [
      [
        PermissionTypes.Login,
        PermissionTypes.ReadData,
        PermissionTypes.ViewDashboards,
      ],
      [
        PermissionTypes.Login,
        PermissionTypes.ReadClientFile,
        PermissionTypes.WriteClientFile,
      ],
    ];
    const hasMinPermissions = validMinimumPermissionSets.some(
      (permissionSet) => {
        return permissionSet.every((permission) => {
          return this.effectivePermissions.filterByType(permission).nonEmpty();
        });
      }
    );
    if (!hasMinPermissions) {
      this.permissionsError = true;
      this.errorTracker.trackError({
        error: new Error(`Insufficient Permissions`),
        errorType: ErrorTypes.MINIMUM_PERMISSIONS_NOT_PRESENT,
      });
    }
  };

  @computed
  private get canManageThisDomain() {
    return this.effectivePermissions
      .filterByType(PermissionTypes.ManageDomain)
      .validFor(this.domain)
      .nonEmpty();
  }

  @computed
  public get canManageAliases(): boolean {
    return this.effectivePermissions
      .filterByType(PermissionTypes.ManageCompany)
      .validFor(this.domain)
      .nonEmpty();
  }

  // Administration

  @computed
  public get canManageAllDomains(): boolean {
    return !!this.effectivePermissions
      .filterByType(PermissionTypes.ManageDomain)
      .map((ap) => ap.permission.domain === null).length;
  }

  @computed
  public get userPermissionsCanManageAllDomains(): boolean {
    return !!this.effectivePermissions
      .filterByType(PermissionTypes.ManageDomain)
      .map((ap) => ap.permission.domain === null).length;
  }

  public canChangeUserStatus = (): boolean => {
    return this.effectivePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.ManageUsers)
      .nonEmpty();
  };

  // Currently reporting-line flag can't be changed/set for superadmins and admins, later we might allow this
  public canSeePrivateUserConfig = (): boolean => {
    return this.effectivePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.ManageUsers)
      .nonEmpty();
  };

  public canManageOwnUserAccount = (): boolean => {
    const domainPermissions = this.effectivePermissions.validFor(this.domain);
    return (
      domainPermissions
        .filterByType(PermissionTypes.ManageCompany)
        .nonEmpty() &&
      domainPermissions.filterByType(PermissionTypes.ManageUsers).nonEmpty()
    );
  };

  // Reports
  @computed
  private get exportReportsPermissionForExecutorRole() {
    return this.executorRolePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.ExportReports)
      .first()?.permission;
  }

  public canExportReports = (): boolean => {
    return this.exportReportsPermissionForExecutorRole !== undefined;
  };

  // Upload data
  public canUploadData(): boolean {
    return this.effectivePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.WriteClientFile)
      .nonEmpty();
  }

  // Delete data
  public canDeleteData(): boolean {
    return [PermissionTypes.DeleteClientFile].some((p) =>
      this.effectivePermissions.validFor(this.domain).filterByType(p).nonEmpty()
    );
  }

  // Download data
  public canDownloadClientData(): boolean {
    return [PermissionTypes.DownloadClientFile].some((p) =>
      this.effectivePermissions.validFor(this.domain).filterByType(p).nonEmpty()
    );
  }

  private reservedRoleNameToTranslatedNameMap: Record<
    ReservedRoleNames,
    string
  > = {
    [ReservedRoleNames.GLOBAL_ACCESS]: i18n.t(
      `common:permissions.reservedRoleNames.${ReservedRoleNames.GLOBAL_ACCESS}`
    ),
    [ReservedRoleNames.SUPERADMIN]: i18n.t(
      `common:permissions.reservedRoleNames.${ReservedRoleNames.SUPERADMIN}`
    ),
    [ReservedRoleNames.ADMIN]: i18n.t(
      `common:permissions.reservedRoleNames.${ReservedRoleNames.ADMIN}`
    ),
  };

  public isReservedRoleName = (name: string) => {
    const reservedRoleNames = enumValues(ReservedRoleNames);
    if (reservedRoleNames.some((n) => n.toLowerCase() === name.toLowerCase())) {
      return true;
    } else {
      return false;
    }
  };

  private getReservedRoleTranslatedName = (name: ReservedRoleNames) => {
    return this.reservedRoleNameToTranslatedNameMap[name];
  };

  public getNonUserRoleName = (nonUserRole: Role) => {
    return nonUserRole && this.isReservedRoleName(nonUserRole.name)
      ? this.getReservedRoleTranslatedName(
          nonUserRole.name as ReservedRoleNames
        )
      : (nonUserRole?.name ?? '???');
  };

  public isRoleEditable = (
    roleWithPermissions: RoleWithPermissions
  ): boolean => {
    const uneditableRoleNames = [
      ReservedRoleNames.SUPERADMIN,
      ReservedRoleNames.ADMIN,
      ReservedRoleNames.GLOBAL_ACCESS,
    ];
    const { role, directPermissions } = roleWithPermissions;
    const isSpecialUneditableRole = Boolean(
      uneditableRoleNames.find(
        (r) => r.toLowerCase() === role.name.toLowerCase()
      )
    );
    const isAdminRole = Boolean(
      directPermissions.findByType(PermissionTypes.GrantPermissions)
    );
    const canCurrentRoleEditSelectedRole = !this.effectivePermissions
      ?.findByType(PermissionTypes.GrantPermissions)
      ?.permission.excludedRoles.includes(role.id);
    return (
      !isAdminRole && !isSpecialUneditableRole && canCurrentRoleEditSelectedRole
    );
  };

  public isRoleDeletable = (
    roleWithPermissions: RoleWithPermissions
  ): boolean => {
    return this.isRoleEditable(roleWithPermissions);
  };

  public isRoleCopyable = (
    roleWithPermissions: RoleWithPermissions
  ): boolean => {
    const uncopyableRoleName = [
      ReservedRoleNames.SUPERADMIN,
      ReservedRoleNames.ADMIN,
    ];
    const { role } = roleWithPermissions;
    const isSpecialUncopyableRole = Boolean(
      uncopyableRoleName.find(
        (r) => r.toLowerCase() === role.name.toLowerCase()
      )
    );
    return !isSpecialUncopyableRole;
  };

  public canViewRoleScopeForRole = (roleId: string): boolean => {
    return this.effectivePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.ViewRoleScope)
      .some((r) => {
        const { role, excludedRoles } = r.permission;
        const isTargetRoleExcluded = excludedRoles.includes(roleId);
        const canViewAllRoles = role === null;
        const canViewTargetRole = role === roleId;
        return !isTargetRoleExcluded && (canViewAllRoles || canViewTargetRole);
      });
  };

  public isRoleEditableScopeForRole = (roleId: string): boolean => {
    return this.effectivePermissions
      .validFor(this.domain)
      .filterByType(PermissionTypes.EditRoleScope)
      .some((r) => {
        const { role, excludedRoles } = r.permission;
        const isTargetRoleExcluded = excludedRoles.includes(roleId);
        const canEditAllRoles = role === null;
        const canEditTargetRole = role === roleId;
        return !isTargetRoleExcluded && (canEditAllRoles || canEditTargetRole);
      });
  };

  public canAccessMyFilterGroups = (): boolean => {
    const effectiveRoleId = this.effectiveRoleId;
    return (
      this.canManageAllDomains ||
      (this.canViewRoleScopeForRole(effectiveRoleId) &&
        this.isRoleEditableScopeForRole(effectiveRoleId))
    );
  };

  private isSuperAdmin = (): boolean => {
    return this.effectivePermissions
      .filterByType(PermissionTypes.AssumeRole)
      .some((p) => p.permission.roles === null);
  };

  public canReadUserPermissions = (): boolean => {
    // all superadmins
    return this.canManageAllDomains || this.isSuperAdmin();
  };

  public canWriteUserPermissions = (): boolean => {
    // all superadmins
    return this.canManageAllDomains || this.isSuperAdmin();
  };

  public canReadRoleSettings = (): boolean => {
    // all superadmins
    return this.canManageAllDomains || this.isSuperAdmin();
  };

  public canWriteRoleSettings = (): boolean => {
    // all superadmins
    return this.canManageAllDomains || this.isSuperAdmin();
  };

  public canViewDatapipelineDag = (): boolean => {
    return this.canManageThisDomain;
  };
}
